// elekt.io's home page map: countries shaded by how soon the next national election
// is. No tiles: the shapes are a checked-in Natural Earth 110m file (public domain,
// app/assets/geo/elekt_world_110m.json), so the map loads one small file from our
// own origin and nothing from a third party.
//
// The server writes the rows (#ek-map-data: country, shade, the next election's
// name and date, and the URLs) and the colours live in CSS custom properties, so
// the shading, the legend and the dark theme share one source.
//
// Accessibility: every country with a date is a link you can Tab to and open with
// Enter (the rest are reachable by mouse and touch), the container describes itself,
// and the same list is on the page under the map ("Show these countries as a list").
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "./elekt/map.css"

const container = document.getElementById("ek-map")
const dataNode = document.getElementById("ek-map-data")

if (container && dataNode) boot().catch(() => showFallback())

function showFallback() {
    container.replaceChildren(Object.assign(document.createElement("p"), {
        className: "ek-map-fallback",
        textContent: "The map could not be loaded. The list of countries below has the same information."
    }))
}

async function boot() {
    const rows = JSON.parse(dataNode.textContent)
    const byIso = new Map(rows.map((row) => [row.i, row]))
    const response = await fetch(container.dataset.geometryUrl)
    if (!response.ok) throw new Error("geometry")
    const geometry = await response.json()

    container.replaceChildren()
    const map = L.map(container, {
        minZoom: 0, maxZoom: 6, zoomSnap: 0.25, worldCopyJump: false, attributionControl: true, zoomControl: false,
        maxBoundsViscosity: 1, scrollWheelZoom: false, renderer: L.svg()
    })
    // Top right: on a wide screen the headline sits over the left of the map.
    L.control.zoom({ position: "topright" }).addTo(map)
    map.attributionControl.setPrefix(false)
    map.attributionControl.addAttribution("Shapes: Natural Earth (public domain)")
    // A page can open the map on one part of the world (data-bounds: [[south, west], [north, east]]); the home page
    // shows all of it.
    const world = container.dataset.bounds ? JSON.parse(container.dataset.bounds) : [[-56, -170], [80, 180]]
    const regional = Boolean(container.dataset.bounds)
    // On a wide screen the home page sets its headline in the ocean on the left
    // ([data-ek-map-inset], absolutely placed over the map): the world is fitted into
    // the space to its right, and the pannable area grows west to match.
    const inset = () => {
        const overlay = document.querySelector("[data-ek-map-inset]")
        if (!overlay || getComputedStyle(overlay).position !== "absolute") return 0
        return Math.max(0, Math.round(overlay.getBoundingClientRect().right - container.getBoundingClientRect().left))
    }
    let refitting = false
    const fit = () => {
        refitting = true
        map.setMaxBounds(null)
        map.setMinZoom(0)
        map.fitBounds(world, { animate: false, paddingTopLeft: [inset(), 0] })
        // Never zoom out past "the whole world fits": on a phone that is a smaller zoom
        // than on a desktop, so it is measured here rather than fixed.
        map.setMinZoom(regional ? Math.max(0, map.getZoom() - 1) : map.getZoom())
        const west = map.containerPointToLatLng([0, 0]).lng
        map.setMaxBounds([[-62, Math.min(-200, west - 2)], [88, 200]])
        refitting = false
    }
    fit()
    let touched = false
    map.on("zoomstart movestart", () => { if (!refitting) touched = true })
    window.addEventListener("resize", () => {
        map.invalidateSize()
        if (!touched) fit()
    })
    // Scroll-wheel zoom would trap the page's own scrolling; opt in with a click.
    map.on("focus", () => map.scrollWheelZoom.enable())
    map.on("blur", () => map.scrollWheelZoom.disable())

    // The shades a page uses: the home page's "how soon" keys, or a prefixed set (the United States: "us-both", ...).
    const prefix = container.dataset.shadePrefix || ""
    const colour = (key) => getComputedStyle(container).getPropertyValue(`--ek-map-${["none", "edge", "hover", "ocean"].includes(key) ? key : prefix + key}`).trim()
    const styleFor = (iso) => {
        const row = byIso.get(iso)
        return { fillColor: colour(row ? row.s : "none"), fillOpacity: 1, color: colour("edge"), weight: 0.6 }
    }
    const hoverStyle = { weight: 2, color: colour("hover") }

    const layers = []
    // The links in a card: the election it is about and the country's own page.
    const links = (row) => {
        const box = document.createElement("span")
        // A row may name its own links (a state's races); else the election and the country.
        if (row.l) {
            row.l.forEach((item, index) => {
                if (index) box.append(document.createElement("br"))
                box.append(Object.assign(document.createElement("a"), { href: item.u, textContent: item.t }))
            })
            return box
        }
        const election = document.createElement("a")
        election.href = row.u
        election.textContent = "Open the election"
        const country = document.createElement("a")
        country.href = row.p
        country.textContent = `All of ${row.n}`
        box.append(election, " · ", country)
        return box
    }
    const card = (iso, name) => {
        const row = byIso.get(iso)
        const box = document.createElement("div")
        box.className = "ek-map-card"
        const title = document.createElement("strong")
        // The flag as an image (the same on every platform); the emoji only where we hold none.
        if (row?.f) {
            const flag = Object.assign(document.createElement("img"), { src: row.f, alt: "", width: 20, height: 20, className: "ek-map-flag" })
            title.append(flag, ` ${name}`)
        } else {
            title.textContent = `${row ? `${row.c || ""} ` : ""}${name}`.trim()
        }
        box.append(title)
        const line = document.createElement("span")
        line.textContent = row ? (row.t ? `${row.e} - ${row.d} (${row.t}${row.x ? ", indirect" : ""})` : `${row.e} - ${row.d}`) : "No national election date known"
        box.append(document.createElement("br"), line)
        return { box, row }
    }

    L.geoJSON(geometry, {
        style: (feature) => styleFor(feature.properties.i),
        onEachFeature: (feature, layer) => {
            const iso = feature.properties.i
            if (!iso) return
            const row = byIso.get(iso)
            const { box } = card(iso, feature.properties.n)
            layer.bindTooltip(box.cloneNode(true), { sticky: true, direction: "top", className: "ek-map-tip" })
            const popup = box.cloneNode(true)
            // The country page for a code is /fra, which redirects to the canonical /france.
            const target = row ? links(row) : Object.assign(document.createElement("a"), { href: `/${iso.toLowerCase()}`, textContent: `Open ${feature.properties.n}` })
            popup.append(document.createElement("br"), target)
            layer.bindPopup(popup, { className: "ek-map-pop" })
            layer.on("mouseover", () => layer.setStyle(hoverStyle))
            layer.on("mouseout", () => layer.setStyle(styleFor(iso)))
            layers.push({ iso, layer, row, name: feature.properties.n })
        }
    }).addTo(map)

    // Countries with a date that the 110m shapes leave out (small states and islands)
    // get a dot where the server says their centre is.
    const drawn = new Set(layers.map((entry) => entry.iso))
    for (const row of rows) {
        if (drawn.has(row.i) || row.y == null) continue
        const dot = L.circleMarker([row.y, row.z], { radius: 6, fillColor: colour(row.s), fillOpacity: 1, color: colour("hover"), weight: 1.5 }).addTo(map)
        const { box } = card(row.i, row.n)
        dot.bindTooltip(box.cloneNode(true), { direction: "top", className: "ek-map-tip" })
        const popup = box.cloneNode(true)
        popup.append(document.createElement("br"), links(row))
        dot.bindPopup(popup, { className: "ek-map-pop" })
        layers.push({ iso: row.i, layer: dot, row, name: row.n })
    }

    // Keyboard: the countries that have something to say are real tab stops.
    for (const { layer, row, name } of layers) {
        if (!row) continue
        const element = layer.getElement?.()
        if (!element) continue
        element.setAttribute("tabindex", "0")
        element.setAttribute("role", "link")
        element.setAttribute("aria-label", `${name}: ${row.e}, ${row.d}, ${row.t}${row.x ? ", indirect election" : ""}`)
        element.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                window.location.assign(row.u || row.p)
            }
        })
        element.addEventListener("focus", () => { layer.setStyle?.(hoverStyle); layer.openTooltip?.() })
        element.addEventListener("blur", () => { layer.setStyle?.(styleFor(row.i)); layer.closeTooltip?.() })
    }

    // Colours come from CSS, so a change of colour scheme has to repaint.
    const repaint = () => {
        for (const { layer, iso } of layers) layer.setStyle?.(layer.setRadius ? { fillColor: colour(byIso.get(iso)?.s || "none"), color: colour("hover") } : styleFor(iso))
    }
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => setTimeout(repaint, 50))
}
